import { useEffect, useState } from "react";
import { Network, Landmark, FileText, AlertTriangle, TrendingUp, Building2, Loader2 } from "lucide-react";
import { useSociete } from "../contexts/SocieteContext";
import PageHeader from "../components/PageHeader";
import GroupOrgChart from "../components/GroupOrgChart";

const eur = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const TYPE_BADGE = {
  holding: "bg-[#f7c935]/15 text-[#f7c935] border-[#f7c935]/30",
  filiale: "bg-sky-500/10 text-sky-300 border-sky-500/20",
};

export default function GroupConsolidationPage() {
  const { societeApi, currentSociete } = useSociete();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentSociete) return;
    setLoading(true);
    setError("");
    societeApi()
      .get("/group/consolidation")
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Erreur de chargement"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSociete?.societe_id]);

  const totals = data?.totaux_eur || {};
  const cards = [
    { key: "tresorerie", label: "Trésorerie nette", icon: Landmark, color: "text-emerald-400" },
    { key: "ca_facture", label: "CA facturé", icon: FileText, color: "text-sky-400" },
    { key: "impayes", label: "Impayés", icon: AlertTriangle, color: "text-rose-400" },
    { key: "resultat_compta", label: "Résultat comptable", icon: TrendingUp, color: "text-[#f7c935]" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="group-consolidation-page">
      <PageHeader
        icon={<Network />}
        title="Groupe — Consolidation"
        description={
          data?.holding
            ? `Vue consolidée de ${data.holding.name} et de ses filiales (montants convertis en EUR).`
            : "Tableau de bord financier consolidé du groupe, réservé aux sociétés holding."
        }
        testId="group-consolidation-header"
      />

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm p-8">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la consolidation…
        </div>
      ) : error ? (
        <div
          className="bg-[#14101c] border border-amber-500/20 rounded-xl p-6 flex items-start gap-3 max-w-2xl"
          data-testid="group-not-holding"
        >
          <Building2 className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-white font-bold text-sm mb-1">Consolidation indisponible</div>
            <p className="text-slate-400 text-xs leading-relaxed">{error}</p>
            <p className="text-slate-500 text-[11px] mt-2">
              Sélectionnez la société holding dans le sélecteur de sociétés (sidebar), ou marquez une société comme
              « Holding » dans Sociétés → Modifier.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {cards.map((c) => (
              <div key={c.key} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`group-total-${c.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <c.icon className={`w-4 h-4 ${c.color}`} />
                  <span className="text-[11px] uppercase tracking-wide text-slate-500">{c.label}</span>
                </div>
                <div className={`text-2xl font-black ${c.color}`}>{eur(totals[c.key])}</div>
              </div>
            ))}
          </div>

          <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 mb-6" data-testid="group-org-section">
            <div className="text-sm font-bold text-white mb-4">Organigramme du groupe</div>
            <GroupOrgChart societes={data.societes} />
          </div>

          <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <span className="text-sm font-bold text-white">Sociétés du périmètre ({data.societes.length})</span>
              <span className="text-[11px] text-slate-500">{data.nb_previsionnels} prévisionnel(s) dans le groupe</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-white/5">
                    <th className="px-4 py-2.5">Société</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Société mère</th>
                    <th className="px-4 py-2.5 text-right">Trésorerie</th>
                    <th className="px-4 py-2.5 text-right">CA facturé</th>
                    <th className="px-4 py-2.5 text-right">Impayés</th>
                    <th className="px-4 py-2.5 text-right">Résultat</th>
                    <th className="px-4 py-2.5 text-right">Factures</th>
                  </tr>
                </thead>
                <tbody>
                  {data.societes.map((s) => {
                    const parent = data.societes.find((p) => p.societe_id === s.parent_societe_id);
                    return (
                      <tr key={s.societe_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`group-societe-row-${s.societe_id}`}>
                        <td className="px-4 py-3 font-bold text-white">
                          <span className="inline-flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color || "#3b82f6" }} />
                            {s.name}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${TYPE_BADGE[s.type] || TYPE_BADGE.filiale}`}>
                            {s.type === "holding" ? "Holding" : "Filiale"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{parent?.name || "—"}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${s.tresorerie_eur < 0 ? "text-rose-400" : "text-emerald-400"}`}>{eur(s.tresorerie_eur)}</td>
                        <td className="px-4 py-3 text-right text-slate-300">{eur(s.ca_facture_eur)}</td>
                        <td className={`px-4 py-3 text-right ${s.impayes_eur > 0 ? "text-rose-400" : "text-slate-500"}`}>{eur(s.impayes_eur)}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${s.resultat_compta_eur < 0 ? "text-rose-400" : "text-[#f7c935]"}`}>{eur(s.resultat_compta_eur)}</td>
                        <td className="px-4 py-3 text-right text-slate-400">{s.nb_factures}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
